import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Users, Clock, PlayCircle, X, Maximize2, Minimize2 } from 'lucide-react';
interface SlideSnakLifecycleFitProps {
  onContinue: () => void;
}
const PHASES = [{
  phase: 'Launch',
  note: 'High novelty',
  mindset: 'I need to understand this.',
  icon: Rocket,
  accent: 'muted' as const,
  points: ['High willingness to invest time', 'Longer and more complex content is acceptable', 'Disease education, MOA, evidence and differentiation'],
  verdict: null
}, {
  phase: 'Mature',
  note: 'Familiarity increases',
  mindset: 'Tell me what is relevant to my practice.',
  icon: Users,
  accent: 'mid' as const,
  points: ['Lower willingness to repeat the learning effort', 'Shorter, targeted and personalized content'],
  verdict: 'Snaks become increasingly valuable'
}, {
  phase: 'Established',
  note: 'Attention is lowest',
  mindset: 'Remind me quickly why this still matters.',
  icon: Clock,
  accent: 'strong' as const,
  points: ['Very low tolerance for long-form content', 'Frequent, short and rewarding activation', 'Hooks, quizzes, proof points, reminders and CTAs'],
  verdict: 'Snaks are most essential'
}];
const SURFACE = {
  muted: 'bg-navy-900/50 border-navy-700',
  mid: 'bg-navy-700/40 border-navy-600',
  strong: 'bg-teal-400/[0.07] border-teal-400/40'
};
const CHIP = {
  muted: 'bg-navy-700 text-white/50',
  mid: 'bg-sky-400/15 text-sky-400',
  strong: 'bg-teal-400/15 text-teal-400'
};
const DOT = {
  muted: 'bg-white/30',
  mid: 'bg-sky-400',
  strong: 'bg-teal-400'
};
export function SlideSnakLifecycleFit({
  onContinue
}: SlideSnakLifecycleFitProps) {
  const [stage, setStage] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expandedView, setExpandedView] = useState<'before' | 'after' | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };
  useEffect(() => {
    const timers = [250, 750, 1250, 1900].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-1272" data-mp="f5niujwthf1x">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-1273" data-mp="j66aacussj3l">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1274" data-mp="t5ez6rat37j3">
          Where Snaks Work Best
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1275" data-mp="2pygsb02x4uua">
          As novelty declines, content must get shorter and more frequent
        </h2>
        <p className="text-lg text-white/60" data-id="element-1276" data-mp="2nch9iptjfdun">
          The effort an HCP is willing to invest changes across the brand
          lifecycle.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full mt-6 space-y-5" data-id="element-1277" data-mp="2ijxxhxai0qry">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1278" data-mp="2dklbtfvd5pqy">
          {PHASES.map((phase, idx) => {
          const Icon = phase.icon;
          const visible = stage >= idx + 1;
          return <motion.div key={phase.phase} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : 16
          }} className={`rounded-2xl border p-5 flex flex-col ${SURFACE[phase.accent]}`} data-id="element-1279" data-mp="31crzlqn0woer" data-mpi={idx}>
                <div className="flex items-center gap-3 mb-4" data-id="element-1280" data-mp="1fzfgq60fgbu7">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${CHIP[phase.accent]}`} data-id="element-1281" data-mp="1fxumyilxswxe">
                    <Icon className="w-5 h-5" data-id="element-1282" data-mp="39kbnjntibyd9" />
                  </div>
                  <div data-id="element-1283" data-mp="2uvfebnfpsfb2">
                    <h3 className="text-white font-semibold text-lg leading-tight" data-id="element-1284" data-mp="2uvynaendztcy">
                      {phase.phase}
                    </h3>
                    <p className="text-white/40 text-xs" data-id="element-1285" data-mp="2zs5u09nlx8vy">{phase.note}</p>
                  </div>
                </div>

                <p className="text-white/85 text-sm font-serif italic border-l-2 border-white/15 pl-3 mb-4 leading-snug" data-id="element-1286" data-mp="3sixl4sptf7cf">
                  “{phase.mindset}”
                </p>

                <ul className="space-y-2 flex-1" data-id="element-1287" data-mp="3dnrfi6umtajm">
                  {phase.points.map(point => <li key={point} className="flex items-start gap-2.5 text-white/70 text-[13px] leading-snug" data-id="element-1288" data-mp="3imujdnyvlaq0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${DOT[phase.accent]}`} data-id="element-1289" data-mp="2775w0vhpm9oo" />
                      {point}
                    </li>)}
                </ul>

                {phase.verdict && <p className={`mt-4 pt-3 border-t text-sm font-semibold ${phase.accent === 'strong' ? 'border-teal-400/20 text-teal-400' : 'border-navy-600 text-sky-400'}`} data-id="element-1290" data-mp="y7cw5stnlz8r">
                    {phase.verdict}
                  </p>}
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl px-7 py-5 flex items-center justify-between gap-4" data-id="element-1291" data-mp="fbmp3r6v6co0">
          <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-1292" data-mp="1hvm9ced1is8o">
            At launch, HCPs work to understand the brand.{' '}
            <span className="text-teal-400 font-semibold" data-id="element-1293" data-mp="1mtnsezagurk5">
              Later, the content has to work to earn their attention.
            </span>
          </p>
          <button onClick={() => setVideoOpen(true)} className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-teal-400/10 text-teal-400 hover:bg-teal-400/20 border border-teal-400/30 transition-colors text-sm font-semibold" data-id="element-1294" data-mp="1wkig883d1z7p">
            <PlayCircle className="w-4 h-4" data-id="element-1295" data-mp="18w90nnwqq7p0" />
            See Example
          </button>
        </motion.div>
      </div>

      {/* Video Modal */}
      {videoOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-navy-900/95 backdrop-blur-sm" data-id="element-1296" data-mp="1dspla84qe3xc">
          <div className="relative w-full max-w-7xl bg-navy-800 rounded-2xl border border-navy-600 shadow-2xl overflow-hidden flex flex-col max-h-full" data-id="element-1297" data-mp="2p9z36q87onwj">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-700 bg-navy-900 shrink-0" data-id="element-1298" data-mp="dmcb6m460d2b">
              <p className="text-white font-semibold" data-id="element-1299" data-mp="8q560vjlf5lf">
                Distilled, not diluted — from static slide to interactive Snak
              </p>
              <button onClick={() => {
            setVideoOpen(false);
            setExpandedView(null);
            setIsPlaying(false);
          }} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" aria-label="Close example" data-id="element-1300" data-mp="3v2c5yp20qsjl">
                <X className="w-5 h-5" data-id="element-1301" data-mp="3c4hpjpyk9er6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 flex flex-col" data-id="element-1302" data-mp="3c6m3z1u35w5p">
              <div className={`grid gap-8 flex-1 ${expandedView ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`} data-id="element-1303" data-mp="1fbpl91frbsk3">
                {/* Before */}
                {(!expandedView || expandedView === 'before') && <div className="space-y-3 flex flex-col h-full" data-id="element-1304" data-mp="j8kbwcgp69qg">
                    <div className="flex items-center justify-between" data-id="element-1305" data-mp="o5u0xr06cjdr">
                      <div className="flex items-center gap-2" data-id="element-1306" data-mp="9iiprq7wn1e6">
                        <span className="px-2.5 py-1 rounded bg-rose-500/10 text-rose-400 text-[10px] font-bold uppercase tracking-widest" data-id="element-1307" data-mp="9fwi7ql0klvy">
                          Before
                        </span>
                        <p className="text-white/80 text-sm font-medium" data-id="element-1308" data-mp="jnj6d0y7pac">
                          Static, complex, overwhelming
                        </p>
                      </div>
                      <button onClick={() => setExpandedView(expandedView === 'before' ? null : 'before')} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" aria-label={expandedView === 'before' ? "Minimize" : "Expand"} data-id="element-1309" data-mp="3mqbzlensw7iu">
                        {expandedView === 'before' ? <Minimize2 className="w-4 h-4" data-id="element-1310" data-mp="3aejdsrscfjqk" /> : <Maximize2 className="w-4 h-4" data-id="element-1311" data-mp="35f6ugh0okusq" />}
                      </button>
                    </div>
                    <div className={`w-full bg-white rounded-xl border border-navy-600 overflow-hidden flex items-center justify-center p-2 relative group flex-1 ${expandedView === 'before' ? 'min-h-[60vh]' : 'aspect-[16/9]'}`} data-id="element-1312" data-mp="3k4l2nlsit66j">
                      <img src="https://cdn.magicpatterns.com/uploads/v4CwvkSh7mpxhw8kTWC58d/Screenshot_2026-08-06_at_09.03.50.png" alt="Complex static slide" className="w-full h-full object-contain cursor-pointer" onClick={() => setExpandedView(expandedView === 'before' ? null : 'before')} data-id="element-1313" data-mp="3t3g98yzh8iad" />
                    </div>
                  </div>}

                {/* After */}
                {(!expandedView || expandedView === 'after') && <div className="space-y-3 flex flex-col h-full" data-id="element-1314" data-mp="2xvueb6js4gmj">
                    <div className="flex items-center justify-between" data-id="element-1315" data-mp="2xsyflg03xnx1">
                      <div className="flex items-center gap-2" data-id="element-1316" data-mp="2suwu6of7lcie">
                        <span className="px-2.5 py-1 rounded bg-teal-400/10 text-teal-400 text-[10px] font-bold uppercase tracking-widest" data-id="element-1317" data-mp="2nwd9ssg9k8s5">
                          After
                        </span>
                        <p className="text-white/80 text-sm font-medium" data-id="element-1318" data-mp="3gkr5v2nskv12">
                          Snakkerfied: stepped, animated, clear
                        </p>
                      </div>
                      <button onClick={() => setExpandedView(expandedView === 'after' ? null : 'after')} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors" aria-label={expandedView === 'after' ? "Minimize" : "Expand"} data-id="element-1319" data-mp="36v7lfskfkt43">
                        {expandedView === 'after' ? <Minimize2 className="w-4 h-4" data-id="element-1320" data-mp="1h3h0hrwjzy5y" /> : <Maximize2 className="w-4 h-4" data-id="element-1321" data-mp="1h4idw311kwqu" />}
                      </button>
                    </div>
                    <div className={`relative w-full bg-black rounded-xl border border-teal-400/30 overflow-hidden shadow-[0_0_30px_rgba(45,212,191,0.1)] group flex-1 ${expandedView === 'after' ? 'min-h-[60vh]' : 'aspect-[16/9]'}`} data-id="element-1322" data-mp="1m2afjorhnx88">
                      <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/wRPYSkZKp4cjTs6A9ycSrB/IL-13%20Tralokinumab%20Binds%20IL-13%20with%20High%20Affinity.mp4" controls={isPlaying} loop muted onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} className="w-full h-full object-contain" data-id="element-1323" data-mp="201tfvve2649z" />
                      {!isPlaying && <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px] transition-all group-hover:bg-black/30" data-id="element-1324" data-mp="2np9ww1vviclm">
                          <button onClick={handlePlay} className="w-16 h-16 rounded-full bg-teal-400 text-navy-900 flex items-center justify-center shadow-[0_0_30px_rgba(45,212,191,0.4)] hover:scale-105 transition-transform" aria-label="Play Snakkerfied version" data-id="element-1325" data-mp="ircu7ov8yysf">
                            <PlayCircle className="w-8 h-8 ml-1" data-id="element-1326" data-mp="12ekdo138yudd" />
                          </button>
                        </div>}
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}